// Chemin: C:\gestion-copro\src\pages\admin\CoproprietairesPage.tsx
// Session 59 : Ajout bouton "Créer un compte" pour lier un compte Firebase Auth
// Session 73 : Ajout "Réinitialiser et renvoyer" + envoi email automatique
// Session 75 : Sécurité suppressions (corbeille visible selon rôle, "Tout supprimer" Admin SaaS uniquement)
// Session 82 : Synchronisation email Auth/Firestore lors de la modification

import React, { useState, useEffect, useRef } from 'react';
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  Mail,
  Phone,
  Home,
  UserCheck,
  UserX,
  Clock,
  X,
  Save,
  AlertCircle,
  Building2,
  Upload,
  FileSpreadsheet,
  CheckCircle,
  AlertTriangle,
  Copy,
  Check,
  Key,
  UserPlus,
  RefreshCw,
  Send
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import {
  getCoproprietairesAvecTantiemes,
  createCoproprietaire,
  updateCoproprietaire,
  deleteCoproprietaire,
  getCoproprietaireAvecLots,
  getStatistiquesCoproprietaires,
  importCoproprietairesEnMasse,
  deleteAllCoproprietaires,
  CoproprietaireCSVRow,
  ImportResult
} from '../../services/coproprietaire.service';
import { getLotsByCopropriete } from '../../services/lot.service';
import { updateUserEmail } from '../../services/user.service';
import {
  Coproprietaire,
  CoproprietaireCreateData,
  CoproprietaireUpdateData,
  CoproprietaireAvecLots,
  STATUT_COMPTE_LABELS,
  STATUT_COMPTE_COLORS,
  StatutCompteCoproprietaire
} from '../../types/coproprietaire.types';
import { Lot, TYPE_LOT_LABELS, TYPES_LOT_PRINCIPAL } from '../../types/lot.types';

// Type enrichi pour l'affichage
interface CoproprietaireAffichage extends Coproprietaire {
  tantiemesTotal: number;
  lotsDetails: string[];
}

const CoproprietairesPage: React.FC = () => {
  const { userProfile } = useAuth();
  
  // ✅ CORRECTION Session 58 : coproprieteIds est un tableau, pas coproprietaireId
  const coproprieteId = userProfile?.coproprieteIds?.[0];

  // Session 75 : Helpers de permission pour les suppressions
  const isAdminSaas = userProfile?.isGestionnaireSaas === true;
  const isSecretaire = (userProfile as any)?.fonction === 'secretaire';
  const isGestionnaire = userProfile?.role === 'gestionnaire' || userProfile?.role === 'admin_saas';
  
  // Peut supprimer un copropriétaire : Secrétaire, Gestionnaire ou Admin SaaS
  const canDeleteCoproprietaire = isAdminSaas || isGestionnaire || isSecretaire;
  
  // Peut tout supprimer : Admin SaaS uniquement (action dangereuse)
  const canDeleteAll = isAdminSaas;
  
  // États principaux
  const [coproprietaires, setCoproprietaires] = useState<CoproprietaireAffichage[]>([]);
  const [lots, setLots] = useState<Lot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // États statistiques
  const [stats, setStats] = useState({
    total: 0,
    avecCompteActif: 0,
    invites: 0,
    nonInvites: 0,
    avecEmail: 0,
    sansEmail: 0
  });
  
  // États modales
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedCoproprietaire, setSelectedCoproprietaire] = useState<CoproprietaireAffichage | null>(null);
  const [coproprietaireDetail, setCoproprietaireDetail] = useState<CoproprietaireAvecLots | null>(null);
  
  // États formulaire
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresseCorrespondance: '',
    lotIds: [] as string[],
    notes: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // États Import CSV
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvData, setCsvData] = useState<CoproprietaireCSVRow[]>([]);
  const [csvFileName, setCsvFileName] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // État suppression en masse
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);

  // État copie prompt
  const [promptCopied, setPromptCopied] = useState(false);

  // Session 59 : États pour création de compte
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [createAccountLoading, setCreateAccountLoading] = useState(false);
  const [createAccountSuccess, setCreateAccountSuccess] = useState(false);
  const [createAccountError, setCreateAccountError] = useState<string | null>(null);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [credentialsCopied, setCredentialsCopied] = useState(false);
  const [accountAlreadyExists, setAccountAlreadyExists] = useState(false);  // Pour proposer liaison
  const [isLinkMode, setIsLinkMode] = useState(false);  // Mode liaison au lieu de création
  const [isResetMode, setIsResetMode] = useState(false);  // Mode réinitialisation
  const [resetLoading, setResetLoading] = useState(false);
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);  // Email envoyé automatiquement

  // Chargement initial
  useEffect(() => {
    if (coproprieteId) {
      loadData();
    }
  }, [coproprieteId]);

  const loadData = async () => {
    if (!coproprieteId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [coprosData, lotsData, statsData] = await Promise.all([
        getCoproprietairesAvecTantiemes(coproprieteId),
        getLotsByCopropriete(coproprieteId),
        getStatistiquesCoproprietaires(coproprieteId)
      ]);
      
      setCoproprietaires(coprosData);
      setLots(lotsData);
      setStats(statsData);
    } catch (err) {
      console.error('Erreur chargement:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Filtrage par recherche
  const coproprietairesFiltres = coproprietaires.filter(copro => {
    const search = searchTerm.toLowerCase();
    return (
      copro.nom.toLowerCase().includes(search) ||
      (copro.prenom && copro.prenom.toLowerCase().includes(search)) ||
      (copro.email && copro.email.toLowerCase().includes(search)) ||
      copro.lotsDetails.some(lot => lot.toLowerCase().includes(search))
    );
  });

  // Lots disponibles (principaux uniquement, non déjà attribués sauf à ce copropriétaire)
  const getLotsDisponibles = () => {
    const lotsPrincipaux = lots.filter(lot => TYPES_LOT_PRINCIPAL.includes(lot.type));
    
    if (selectedCoproprietaire) {
      // En modification : inclure les lots déjà attribués à ce copropriétaire
      return lotsPrincipaux.filter(lot => 
        !lot.proprietaireId || 
        lot.proprietaireId === selectedCoproprietaire.id ||
        formData.lotIds.includes(lot.id)
      );
    }
    
    // En création : uniquement les lots non attribués
    return lotsPrincipaux.filter(lot => !lot.proprietaireId);
  };

  // Ouvrir le formulaire (création ou modification)
  const openFormModal = (copro?: CoproprietaireAffichage) => {
    if (copro) {
      setSelectedCoproprietaire(copro);
      setFormData({
        nom: copro.nom,
        prenom: copro.prenom || '',
        email: copro.email || '',
        telephone: copro.telephone || '',
        adresseCorrespondance: copro.adresseCorrespondance || '',
        lotIds: copro.lotIds || [],
        notes: copro.notes || ''
      });
    } else {
      setSelectedCoproprietaire(null);
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        adresseCorrespondance: '',
        lotIds: [],
        notes: ''
      });
    }
    setFormError(null);
    setShowFormModal(true);
  };

  // Ouvrir le détail
  const openDetailModal = async (copro: CoproprietaireAffichage) => {
    if (!coproprieteId) return;
    
    try {
      const detail = await getCoproprietaireAvecLots(coproprieteId, copro.id);
      setCoproprietaireDetail(detail);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Erreur chargement détail:', err);
    }
  };

  // Ouvrir confirmation de suppression
  const openDeleteConfirm = (copro: CoproprietaireAffichage) => {
    setSelectedCoproprietaire(copro);
    setShowDeleteConfirm(true);
  };

  // Soumettre le formulaire
  // Session 82 : Ajout synchronisation email Auth/Firestore si compte lié
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!coproprieteId) return;
    
    // Validation
    if (!formData.nom.trim()) {
      setFormError('Le nom est obligatoire');
      return;
    }
    
    setFormLoading(true);
    setFormError(null);
    
    try {
      if (selectedCoproprietaire) {
        // Session 82 : Vérifier si l'email a changé ET si un compte est lié
        const emailChanged = formData.email.trim() !== (selectedCoproprietaire.email || '');
        const hasLinkedAccount = !!selectedCoproprietaire.userId;
        
        if (emailChanged && hasLinkedAccount && formData.email.trim()) {
          // Synchroniser l'email avec Firebase Auth via Cloud Function
          try {
            await updateUserEmail({
              userId: selectedCoproprietaire.userId!,
              newEmail: formData.email.trim()
            });
            // La Cloud Function met à jour users + coproprietaires, pas besoin de updateCoproprietaire pour l'email
            // Mais on doit quand même mettre à jour les autres champs
            const updateData: CoproprietaireUpdateData = {
              nom: formData.nom.trim(),
              prenom: formData.prenom.trim() || undefined,
              // email est déjà mis à jour par la Cloud Function
              telephone: formData.telephone.trim() || undefined,
              adresseCorrespondance: formData.adresseCorrespondance.trim() || undefined,
              lotIds: formData.lotIds,
              notes: formData.notes.trim() || undefined
            };
            await updateCoproprietaire(coproprieteId, selectedCoproprietaire.id, updateData);
          } catch (emailError: any) {
            console.error('Erreur synchronisation email:', emailError);
            if (emailError.code === 'functions/already-exists') {
              setFormError('Cette adresse email est déjà utilisée par un autre compte.');
            } else {
              setFormError(`Erreur lors de la mise à jour de l'email : ${emailError.message || 'Erreur inconnue'}`);
            }
            setFormLoading(false);
            return;
          }
        } else {
          // Pas de compte lié ou email inchangé : mise à jour normale
          const updateData: CoproprietaireUpdateData = {
            nom: formData.nom.trim(),
            prenom: formData.prenom.trim() || undefined,
            email: formData.email.trim() || undefined,
            telephone: formData.telephone.trim() || undefined,
            adresseCorrespondance: formData.adresseCorrespondance.trim() || undefined,
            lotIds: formData.lotIds,
            notes: formData.notes.trim() || undefined
          };
          await updateCoproprietaire(coproprieteId, selectedCoproprietaire.id, updateData);
        }
      } else {
        // Création
        const createData: CoproprietaireCreateData = {
          coproprieteId,
          nom: formData.nom.trim(),
          prenom: formData.prenom.trim() || undefined,
          email: formData.email.trim() || undefined,
          telephone: formData.telephone.trim() || undefined,
          adresseCorrespondance: formData.adresseCorrespondance.trim() || undefined,
          lotIds: formData.lotIds,
          notes: formData.notes.trim() || undefined
        };
        
        await createCoproprietaire(createData);
      }
      
      setShowFormModal(false);
      await loadData();
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setFormError('Erreur lors de la sauvegarde');
    } finally {
      setFormLoading(false);
    }
  };

  // Supprimer un copropriétaire
  const handleDelete = async () => {
    if (!coproprieteId || !selectedCoproprietaire) return;
    
    try {
      await deleteCoproprietaire(coproprieteId, selectedCoproprietaire.id);
      setShowDeleteConfirm(false);
      setSelectedCoproprietaire(null);
      await loadData();
    } catch (err) {
      console.error('Erreur suppression:', err);
    }
  };

  // Toggle sélection lot
  const toggleLotSelection = (lotId: string) => {
    setFormData(prev => ({
      ...prev,
      lotIds: prev.lotIds.includes(lotId)
        ? prev.lotIds.filter(id => id !== lotId)
        : [...prev.lotIds, lotId]
    }));
  };

  // Icône statut compte
  const getStatutIcon = (statut: StatutCompteCoproprietaire) => {
    switch (statut) {
      case 'actif': return <UserCheck className="w-4 h-4" />;
      case 'invite': return <Clock className="w-4 h-4" />;
      default: return <UserX className="w-4 h-4" />;
    }
  };

  // ============================================
  // SESSION 59 : CRÉATION DE COMPTE
  // ============================================

  // Générer un mot de passe sécurisé
  const generatePassword = (): string => {
    const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let password = 'Copro2026!';
    for (let i = 0; i < 4; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // Ouvrir la modal de création de compte
  const openCreateAccountModal = () => {
    setGeneratedPassword(generatePassword());
    setCreateAccountSuccess(false);
    setCreateAccountError(null);
    setPasswordCopied(false);
    setCredentialsCopied(false);
    setAccountAlreadyExists(false);
    setIsLinkMode(false);
    setIsResetMode(false);
    setEmailSentSuccess(false);
    setShowCreateAccountModal(true);
  };

  // Ouvrir la modal en mode réinitialisation
  const openResetAccountModal = () => {
    setGeneratedPassword(generatePassword());
    setCreateAccountSuccess(false);
    setCreateAccountError(null);
    setPasswordCopied(false);
    setCredentialsCopied(false);
    setAccountAlreadyExists(false);
    setIsLinkMode(false);
    setIsResetMode(true);
    setEmailSentSuccess(false);
    setShowCreateAccountModal(true);
  };

  // Créer ou lier le compte utilisateur
  const handleCreateAccount = async () => {
    if (!coproprietaireDetail || !coproprieteId) return;
    
    setCreateAccountLoading(true);
    setCreateAccountError(null);
    
    try {
      const createUserAccount = httpsCallable(functions, 'createUserAccount');
      
      const result = await createUserAccount({
        email: coproprietaireDetail.email,
        password: generatedPassword,
        nom: coproprietaireDetail.nom,
        prenom: coproprietaireDetail.prenom || '',
        role: 'coproprietaire',
        coproprieteId: coproprieteId,
        membreId: coproprietaireDetail.id,
        membreCollection: 'coproprietaires'
      });
      
      const data = result.data as { success: boolean; emailSent: boolean; message: string };
      
      setCreateAccountSuccess(true);
      setEmailSentSuccess(data.emailSent);
      setAccountAlreadyExists(false);
      
      // Mettre à jour le détail local
      setCoproprietaireDetail(prev => prev ? {
        ...prev,
        statutCompte: 'actif' as StatutCompteCoproprietaire
      } : null);
      
      // Recharger les données
      await loadData();
      
    } catch (err: any) {
      console.error('Erreur création compte:', err);
      
      // Si le compte existe déjà, proposer la liaison
      if (err.code === 'functions/already-exists') {
        setAccountAlreadyExists(true);
        setCreateAccountError(null);
      } else if (err.message) {
        setCreateAccountError(err.message);
      } else {
        setCreateAccountError('Erreur lors de la création du compte.');
      }
    } finally {
      setCreateAccountLoading(false);
    }
  };

  // Réinitialiser et renvoyer les identifiants
  const handleResetAndResend = async () => {
    if (!coproprietaireDetail || !coproprieteId) return;
    
    setResetLoading(true);
    setCreateAccountError(null);
    
    try {
      const resetAndResendCredentials = httpsCallable(functions, 'resetAndResendCredentials');
      
      const result = await resetAndResendCredentials({
        userId: coproprietaireDetail.userId,
        coproprieteId: coproprieteId,
        newPassword: generatedPassword
      });
      
      const data = result.data as { success: boolean; emailSent: boolean; message: string };
      
      setCreateAccountSuccess(true);
      setEmailSentSuccess(data.emailSent);
      
    } catch (err: any) {
      console.error('Erreur réinitialisation:', err);
      setCreateAccountError(err.message || 'Erreur lors de la réinitialisation.');
    } finally {
      setResetLoading(false);
    }
  };

  // Passer en mode liaison
  const switchToLinkMode = () => {
    setIsLinkMode(true);
    setAccountAlreadyExists(false);
    setCreateAccountError(null);
  };

  // Copier le mot de passe
  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  // Copier les identifiants complets
  const copyCredentials = () => {
    if (!coproprietaireDetail) return;
    const text = `Identifiants Copro Protect\nEmail : ${coproprietaireDetail.email}\nMot de passe : ${generatedPassword}`;
    navigator.clipboard.writeText(text);
    setCredentialsCopied(true);
    setTimeout(() => setCredentialsCopied(false), 2000);
  };

  // ============================================
  // IMPORT CSV
  // ============================================

  // Parser le fichier CSV
  const parseCSV = (content: string): CoproprietaireCSVRow[] => {
    const lines = content.trim().split('\n');
    if (lines.length < 2) return [];

    // Détecter le séparateur
    const firstLine = lines[0];
    const separator = firstLine.includes(';') ? ';' : ',';

    // Extraire les headers
    const headers = lines[0].split(separator).map(h => h.trim().toLowerCase());
    
    // Mapper les colonnes
    const nomIndex = headers.findIndex(h => h === 'nom');
    const prenomIndex = headers.findIndex(h => h === 'prénom' || h === 'prenom');
    const emailIndex = headers.findIndex(h => h === 'email' || h === 'mail');
    const telephoneIndex = headers.findIndex(h => h === 'téléphone' || h === 'telephone' || h === 'tel');
    const lotsIndex = headers.findIndex(h => h === 'lots' || h === 'lot' || h === 'numéros de lots');

    if (nomIndex === -1 || prenomIndex === -1) {
      throw new Error('Colonnes "Nom" et "Prénom" obligatoires');
    }

    const rows: CoproprietaireCSVRow[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = line.split(separator).map(v => v.trim());

      const row: CoproprietaireCSVRow = {
        nom: values[nomIndex] || '',
        prenom: values[prenomIndex] || ''
      };

      if (emailIndex !== -1 && values[emailIndex]) {
        row.email = values[emailIndex];
      }
      if (telephoneIndex !== -1 && values[telephoneIndex]) {
        row.telephone = values[telephoneIndex];
      }
      if (lotsIndex !== -1 && values[lotsIndex]) {
        row.lots = values[lotsIndex];
      }

      // Ignorer les lignes sans nom
      if (row.nom) {
        rows.push(row);
      }
    }

    return rows;
  };

  // Gérer la sélection de fichier
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setCsvFileName(file.name);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = parseCSV(content);
        setCsvData(parsed);
        setShowImportModal(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur lors de la lecture du fichier');
      }
    };
    reader.readAsText(file, 'UTF-8');

    // Reset l'input pour permettre de resélectionner le même fichier
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Lancer l'import
  const handleImport = async () => {
    if (!coproprieteId || csvData.length === 0) return;

    setImportLoading(true);
    try {
      const result = await importCoproprietairesEnMasse(coproprieteId, csvData);
      setImportResult(result);
      await loadData();
    } catch (err) {
      console.error('Erreur import:', err);
      setError('Erreur lors de l\'import');
    } finally {
      setImportLoading(false);
    }
  };

  // Fermer la modale d'import
  const closeImportModal = () => {
    setShowImportModal(false);
    setCsvData([]);
    setCsvFileName('');
    setImportResult(null);
  };

  // Supprimer tous les copropriétaires
  const handleDeleteAll = async () => {
    if (!coproprieteId || deleteAllConfirmText !== 'SUPPRIMER') return;

    setDeleteAllLoading(true);
    try {
      await deleteAllCoproprietaires(coproprieteId);
      setShowDeleteAllConfirm(false);
      setDeleteAllConfirmText('');
      await loadData();
    } catch (err) {
      console.error('Erreur suppression:', err);
    } finally {
      setDeleteAllLoading(false);
    }
  };

  // Copier le prompt
  const copyPrompt = () => {
    const prompt = `À partir de ce document, extrais la liste des copropriétaires.
Génère un fichier CSV avec séparateur point-virgule et ces colonnes exactes :
Nom;Prénom;Email;Téléphone;Lots

Règles :
- Nom = Nom de famille en MAJUSCULES
- Prénom = Prénom avec majuscule initiale
- Email = Adresse email si disponible (vide sinon)
- Téléphone = Numéro de téléphone si disponible (vide sinon)
- Lots = Numéros des lots possédés séparés par des virgules (ex: 1,20,45)

Trie par nom de famille alphabétique.
Ne mets pas d'en-tête de code, génère directement le contenu CSV.`;

    navigator.clipboard.writeText(prompt);
    setPromptCopied(true);
    setTimeout(() => setPromptCopied(false), 2000);
  };

  // Affichage chargement
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // ============================================
  // ÉTAT VIDE - ONBOARDING
  // ============================================
  if (coproprietaires.length === 0 && !searchTerm) {
    return (
      <div className="space-y-6">
        {/* En-tête */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-600" />
            Copropriétaires
          </h1>
          <p className="text-gray-600 mt-1">
            Gestion des copropriétaires et de leurs lots
          </p>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {/* Carte d'onboarding */}
        <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-8">
          <div className="max-w-2xl mx-auto text-center space-y-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
              <Users className="w-8 h-8 text-blue-600" />
            </div>
            
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Aucun copropriétaire enregistré
              </h2>
              <p className="text-gray-600 mt-2">
                Importez votre liste de copropriétaires via un fichier CSV pour commencer.
              </p>
            </div>

            {/* Options d'import */}
            <div className="grid md:grid-cols-2 gap-4 text-left">
              {/* Option 1 : Import CSV */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-gray-900 font-medium">
                  <FileSpreadsheet className="w-5 h-5 text-green-600" />
                  Option 1 : Import CSV
                </div>
                <p className="text-sm text-gray-600">
                  Si vous avez déjà un fichier CSV avec vos copropriétaires.
                </p>
                <p className="text-xs text-gray-500">
                  Format attendu : Nom;Prénom;Email;Téléphone;Lots
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Importer un fichier CSV
                </button>
              </div>

              {/* Option 2 : Extraction IA */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-gray-900 font-medium">
                  <FileSpreadsheet className="w-5 h-5 text-purple-600" />
                  Option 2 : Extraction via Claude.ai
                </div>
                <p className="text-sm text-gray-600">
                  Utilisez Claude.ai pour extraire les copropriétaires depuis un document.
                </p>
                <ol className="text-xs text-gray-500 space-y-1 list-decimal list-inside">
                  <li>Allez sur claude.ai</li>
                  <li>Uploadez votre document (PV AG, liste...)</li>
                  <li>Copiez le prompt ci-dessous</li>
                  <li>Téléchargez le CSV généré</li>
                  <li>Importez-le ici</li>
                </ol>
                <button
                  onClick={copyPrompt}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  {promptCopied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Prompt copié !
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copier le prompt d'extraction
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Séparateur */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">ou</span>
              </div>
            </div>

            {/* Ajout manuel */}
            <button
              onClick={() => openFormModal()}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Ajouter manuellement un copropriétaire
            </button>
          </div>
        </div>

        {/* Modales */}
        {renderFormModal()}
        {renderImportModal()}
      </div>
    );
  }

  // ============================================
  // FONCTIONS DE RENDU DES MODALES
  // ============================================

  function renderFormModal() {
    if (!showFormModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedCoproprietaire ? 'Modifier le copropriétaire' : 'Nouveau copropriétaire'}
            </h2>
            <button
              onClick={() => setShowFormModal(false)}
              className="p-1 text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {formError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-800 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {formError}
              </div>
            )}
            
            {/* Nom / Prénom */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="DUPONT"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prénom
                </label>
                <input
                  type="text"
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Jean"
                />
              </div>
            </div>
            
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="jean.dupont@email.com"
              />
            </div>
            
            {/* Téléphone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone
              </label>
              <input
                type="tel"
                value={formData.telephone}
                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="06 12 34 56 78"
              />
            </div>
            
            {/* Adresse de correspondance */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse de correspondance
              </label>
              <input
                type="text"
                value={formData.adresseCorrespondance}
                onChange={(e) => setFormData({ ...formData, adresseCorrespondance: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Si différente de l'adresse du lot"
              />
            </div>
            
            {/* Sélection des lots */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lots possédés
              </label>
              <div className="border rounded-lg max-h-40 overflow-y-auto">
                {getLotsDisponibles().length === 0 ? (
                  <div className="p-3 text-sm text-gray-500 text-center">
                    Tous les lots sont déjà attribués
                  </div>
                ) : (
                  <div className="divide-y">
                    {getLotsDisponibles().map((lot) => (
                      <label
                        key={lot.id}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.lotIds.includes(lot.id)}
                          onChange={() => toggleLotSelection(lot.id)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div className="flex-1">
                          <span className="font-medium">Lot {lot.numero}</span>
                          <span className="text-gray-500 text-sm ml-2">
                            {TYPE_LOT_LABELS[lot.type]}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500">
                          {lot.tantièmesGeneraux} ‰
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {formData.lotIds.length > 0 && (
                <p className="mt-1 text-sm text-gray-600">
                  {formData.lotIds.length} lot(s) sélectionné(s)
                </p>
              )}
            </div>
            
            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes internes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Notes visibles uniquement par le conseil syndical..."
              />
            </div>
            
            {/* Boutons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => setShowFormModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={formLoading}
              >
                {formLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {selectedCoproprietaire ? 'Mettre à jour' : 'Créer'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  function renderImportModal() {
    if (!showImportModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              Import CSV : {csvFileName}
            </h2>
            <button
              onClick={closeImportModal}
              className="p-1 text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {importResult ? (
              // Résultat de l'import
              <div className="space-y-4">
                <div className={`p-4 rounded-lg flex items-start gap-3 ${
                  importResult.erreurs.length === 0 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-amber-50 border border-amber-200'
                }`}>
                  {importResult.erreurs.length === 0 ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className="font-medium text-gray-900">
                      {importResult.crees} copropriétaire(s) importé(s) sur {importResult.total}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {importResult.lotsTrouves} lot(s) lié(s) automatiquement
                    </p>
                  </div>
                </div>

                {importResult.lotsNonTrouves.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="font-medium text-amber-800">Lots non trouvés :</p>
                    <p className="text-sm text-amber-700 mt-1">
                      {importResult.lotsNonTrouves.join(', ')}
                    </p>
                    <p className="text-xs text-amber-600 mt-2">
                      Ces lots n'existent pas encore. Importez d'abord les lots, puis réimportez les copropriétaires.
                    </p>
                  </div>
                )}

                {importResult.erreurs.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="font-medium text-red-800">Erreurs :</p>
                    <ul className="text-sm text-red-700 mt-1 space-y-1">
                      {importResult.erreurs.map((err, idx) => (
                        <li key={idx}>Ligne {err.ligne} : {err.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              // Prévisualisation
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  <strong>{csvData.length}</strong> copropriétaire(s) détecté(s). Vérifiez les données avant l'import.
                </p>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">#</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Nom</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Prénom</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Email</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Téléphone</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Lots</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {csvData.slice(0, 20).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                          <td className="px-3 py-2 font-medium">{row.nom}</td>
                          <td className="px-3 py-2">{row.prenom}</td>
                          <td className="px-3 py-2 text-gray-600">{row.email || '-'}</td>
                          <td className="px-3 py-2 text-gray-600">{row.telephone || '-'}</td>
                          <td className="px-3 py-2 text-gray-600">{row.lots || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {csvData.length > 20 && (
                    <div className="px-3 py-2 bg-gray-50 text-sm text-gray-500 text-center">
                      ... et {csvData.length - 20} autres lignes
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
            <button
              onClick={closeImportModal}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {importResult ? 'Fermer' : 'Annuler'}
            </button>
            {!importResult && (
              <button
                onClick={handleImport}
                disabled={importLoading || csvData.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {importLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Importer {csvData.length} copropriétaire(s)
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderDetailModal() {
    if (!showDetailModal || !coproprietaireDetail) return null;

    // Session 59 : Condition pour afficher le bouton "Créer un compte"
    const canCreateAccount = coproprietaireDetail.email && 
                             coproprietaireDetail.statutCompte !== 'actif';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">
              Détails du copropriétaire
            </h2>
            <button
              onClick={() => setShowDetailModal(false)}
              className="p-1 text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Identité */}
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {coproprietaireDetail.nom}
                {coproprietaireDetail.prenom && ` ${coproprietaireDetail.prenom}`}
              </h3>
              <span
                className={`inline-flex items-center gap-1 mt-2 px-2 py-1 text-xs font-medium rounded-full ${STATUT_COMPTE_COLORS[coproprietaireDetail.statutCompte]}`}
              >
                {getStatutIcon(coproprietaireDetail.statutCompte)}
                {STATUT_COMPTE_LABELS[coproprietaireDetail.statutCompte]}
              </span>
            </div>
            
            {/* Contact */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Contact</h4>
              {coproprietaireDetail.email && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${coproprietaireDetail.email}`} className="hover:text-blue-600">
                    {coproprietaireDetail.email}
                  </a>
                </div>
              )}
              {coproprietaireDetail.telephone && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4" />
                  <a href={`tel:${coproprietaireDetail.telephone}`} className="hover:text-blue-600">
                    {coproprietaireDetail.telephone}
                  </a>
                </div>
              )}
              {coproprietaireDetail.adresseCorrespondance && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Building2 className="w-4 h-4" />
                  {coproprietaireDetail.adresseCorrespondance}
                </div>
              )}
              {!coproprietaireDetail.email && !coproprietaireDetail.telephone && !coproprietaireDetail.adresseCorrespondance && (
                <p className="text-gray-400">Aucune information de contact</p>
              )}
            </div>
            
            {/* Lots */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Lots possédés ({coproprietaireDetail.lots.length})
              </h4>
              {coproprietaireDetail.lots.length > 0 ? (
                <div className="space-y-2">
                  {coproprietaireDetail.lots.map((lot) => (
                    <div
                      key={lot.id}
                      className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <Home className="w-4 h-4 text-blue-600" />
                        <span className="font-medium">Lot {lot.numero}</span>
                        <span className="text-gray-500 text-sm">
                          {TYPE_LOT_LABELS[lot.type as keyof typeof TYPE_LOT_LABELS] || lot.type}
                        </span>
                      </div>
                      <span className="text-gray-700 font-medium">
                        {lot.tantiemes.toLocaleString('fr-FR')} ‰
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="font-semibold text-gray-700">Total tantièmes</span>
                    <span className="font-bold text-blue-600">
                      {coproprietaireDetail.tantiemesTotal.toLocaleString('fr-FR')} ‰
                    </span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400">Aucun lot attribué</p>
              )}
            </div>
            
            {/* Notes */}
            {coproprietaireDetail.notes && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Notes internes</h4>
                <p className="text-gray-600 bg-amber-50 border border-amber-100 rounded-lg p-3">
                  {coproprietaireDetail.notes}
                </p>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex flex-wrap justify-end gap-3 pt-4 border-t">
              {/* Session 59 : Bouton Créer un compte */}
              {canCreateAccount && (
                <button
                  onClick={openCreateAccountModal}
                  className="inline-flex items-center gap-2 px-4 py-2 text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
                >
                  <UserPlus className="w-4 h-4" />
                  Créer un compte
                </button>
              )}
              {/* Session 73 : Bouton Réinitialiser et renvoyer pour comptes actifs */}
              {coproprietaireDetail.statutCompte === 'actif' && coproprietaireDetail.email && (
                <button
                  onClick={openResetAccountModal}
                  className="inline-flex items-center gap-2 px-4 py-2 text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Réinitialiser et renvoyer
                </button>
              )}
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  const copro = coproprietaires.find(c => c.id === coproprietaireDetail.id);
                  if (copro) openFormModal(copro);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-amber-700 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                Modifier
              </button>
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Session 59 : Modal de création de compte
  function renderCreateAccountModal() {
    if (!showCreateAccountModal || !coproprietaireDetail) return null;

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              {isResetMode ? (
                <>
                  <RefreshCw className="w-5 h-5 text-blue-600" />
                  Réinitialiser et renvoyer les identifiants
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5 text-green-600" />
                  {isLinkMode ? 'Lier un compte existant' : 'Créer un compte utilisateur'}
                </>
              )}
            </h2>
            <button
              onClick={() => setShowCreateAccountModal(false)}
              className="p-1 text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {createAccountSuccess ? (
              // Succès
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-green-800">
                      {isResetMode ? 'Mot de passe réinitialisé !' : isLinkMode ? 'Compte lié avec succès !' : 'Compte créé avec succès !'}
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      {emailSentSuccess 
                        ? 'Les identifiants ont été envoyés automatiquement par email.'
                        : isLinkMode 
                          ? 'Le copropriétaire peut se connecter avec son compte existant.'
                          : 'Le copropriétaire peut maintenant se connecter.'}
                    </p>
                  </div>
                </div>

                {emailSentSuccess && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <Send className="w-5 h-5 text-blue-600" />
                    <p className="text-sm text-blue-800">
                      Email envoyé à <strong>{coproprietaireDetail.email}</strong>
                    </p>
                  </div>
                )}

                {!isLinkMode && (
                  <>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="w-4 h-4" />
                        <span className="font-medium">{coproprietaireDetail.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Key className="w-4 h-4" />
                        <code className="font-mono bg-white px-2 py-1 rounded border">
                          {generatedPassword}
                        </code>
                      </div>
                    </div>

                    <button
                      onClick={copyCredentials}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {credentialsCopied ? (
                        <>
                          <Check className="w-4 h-4" />
                          Identifiants copiés !
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copier les identifiants
                        </>
                      )}
                    </button>

                    <p className="text-xs text-gray-500 text-center">
                      Transmettez ces identifiants au copropriétaire de manière sécurisée.
                    </p>
                  </>
                )}
              </div>
            ) : accountAlreadyExists ? (
              // Compte existant détecté - proposer liaison
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Un compte existe déjà</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Un compte Firebase Auth existe déjà avec l'email <strong>{coproprietaireDetail.email}</strong>.
                    </p>
                  </div>
                </div>

                <p className="text-gray-600">
                  Voulez-vous lier ce compte existant au copropriétaire ?
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCreateAccountModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={switchToLinkMode}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <UserPlus className="w-4 h-4" />
                    Lier ce compte
                  </button>
                </div>
              </div>
            ) : isLinkMode ? (
              // Mode liaison
              <div className="space-y-4">
                <p className="text-gray-600">
                  Le compte existant sera lié à ce copropriétaire :
                </p>

                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="font-medium">{coproprietaireDetail.email}</span>
                </div>

                <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    Le copropriétaire pourra se connecter avec ses identifiants existants.
                  </p>
                </div>

                {createAccountError && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{createAccountError}</p>
                  </div>
                )}
              </div>
            ) : (
              // Formulaire création
              <div className="space-y-4">
                <p className="text-gray-600">
                  Un compte sera créé pour :
                </p>

                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="font-medium">{coproprietaireDetail.email}</span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mot de passe généré
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 font-mono text-lg bg-gray-50 px-3 py-2 rounded-lg border">
                      {generatedPassword}
                    </code>
                    <button
                      onClick={copyPassword}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Copier le mot de passe"
                    >
                      {passwordCopied ? (
                        <Check className="w-5 h-5 text-green-600" />
                      ) : (
                        <Copy className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    {isResetMode 
                      ? 'Un email sera envoyé automatiquement avec le nouveau mot de passe.'
                      : 'Un email sera envoyé automatiquement au copropriétaire avec ses identifiants de connexion.'}
                  </p>
                </div>

                {createAccountError && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-800">{createAccountError}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {!accountAlreadyExists && (
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => setShowCreateAccountModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {createAccountSuccess ? 'Fermer' : 'Annuler'}
              </button>
              {!createAccountSuccess && (
                <button
                  onClick={isResetMode ? handleResetAndResend : handleCreateAccount}
                  disabled={createAccountLoading || resetLoading}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 ${
                    isResetMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {(createAccountLoading || resetLoading) ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      {isResetMode ? 'Réinitialisation...' : isLinkMode ? 'Liaison...' : 'Création...'}
                    </>
                  ) : (
                    <>
                      {isResetMode ? <RefreshCw className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                      {isResetMode ? 'Réinitialiser et envoyer' : isLinkMode ? 'Lier le compte' : 'Créer et envoyer'}
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderDeleteConfirmModal() {
    if (!showDeleteConfirm || !selectedCoproprietaire) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Supprimer ce copropriétaire ?
                </h3>
                <p className="text-gray-600">
                  {selectedCoproprietaire.nom}
                  {selectedCoproprietaire.prenom && ` ${selectedCoproprietaire.prenom}`}
                </p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-6">
              Cette action est irréversible. Les lots associés seront déliés mais conservés.
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderDeleteAllConfirmModal() {
    if (!showDeleteAllConfirm) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Supprimer TOUS les copropriétaires ?
                </h3>
                <p className="text-gray-600">
                  {coproprietaires.length} copropriétaire(s) seront supprimés
                </p>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Cette action est irréversible. Les lots associés seront déliés mais conservés.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tapez <strong>SUPPRIMER</strong> pour confirmer
              </label>
              <input
                type="text"
                value={deleteAllConfirmText}
                onChange={(e) => setDeleteAllConfirmText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="SUPPRIMER"
              />
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteAllConfirm(false);
                  setDeleteAllConfirmText('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAll}
                disabled={deleteAllConfirmText !== 'SUPPRIMER' || deleteAllLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteAllLoading ? 'Suppression...' : 'Tout supprimer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDU PRINCIPAL (avec données)
  // ============================================

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-600" />
            Copropriétaires
          </h1>
          <p className="text-gray-600 mt-1">
            Gestion des copropriétaires et de leurs lots
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Upload className="w-5 h-5" />
            Importer CSV
          </button>
          <button
            onClick={() => openFormModal()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Ajouter
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600">Copropriétaires</div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-green-600">{stats.avecCompteActif}</div>
          <div className="text-sm text-gray-600">Comptes actifs</div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-amber-600">{stats.invites}</div>
          <div className="text-sm text-gray-600">Invitations en attente</div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold text-gray-600">{stats.nonInvites}</div>
          <div className="text-sm text-gray-600">Non invités</div>
        </div>
      </div>

      {/* Barre de recherche + Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par nom, email ou numéro de lot..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        {/* Session 75 : Bouton "Tout supprimer" visible uniquement pour Admin SaaS */}
        {coproprietaires.length > 0 && canDeleteAll && (
          <button
            onClick={() => setShowDeleteAllConfirm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Tout supprimer
          </button>
        )}
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Tableau des copropriétaires */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {coproprietairesFiltres.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm ? (
              <p>Aucun copropriétaire ne correspond à la recherche.</p>
            ) : (
              <div className="space-y-3">
                <Users className="w-12 h-12 mx-auto text-gray-300" />
                <p>Aucun copropriétaire enregistré.</p>
                <button
                  onClick={() => openFormModal()}
                  className="text-blue-600 hover:underline"
                >
                  Ajouter le premier copropriétaire
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Nom</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Contact</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Lots</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900">Tantièmes</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Compte</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {coproprietairesFiltres.map((copro) => (
                  <tr key={copro.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {copro.nom}
                        {copro.prenom && <span className="font-normal"> {copro.prenom}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        {copro.email ? (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Mail className="w-3.5 h-3.5" />
                            {copro.email}
                          </div>
                        ) : null}
                        {copro.telephone ? (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Phone className="w-3.5 h-3.5" />
                            {copro.telephone}
                          </div>
                        ) : null}
                        {!copro.email && !copro.telephone && (
                          <span className="text-sm text-gray-400">Non renseigné</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {copro.lotsDetails.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {copro.lotsDetails.map((lot, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded"
                            >
                              <Home className="w-3 h-3 mr-1" />
                              {lot}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Aucun lot</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-medium text-gray-900">
                        {copro.tantiemesTotal.toLocaleString('fr-FR')} ‰
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${STATUT_COMPTE_COLORS[copro.statutCompte]}`}
                      >
                        {getStatutIcon(copro.statutCompte)}
                        {STATUT_COMPTE_LABELS[copro.statutCompte]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openDetailModal(copro)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Voir détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openFormModal(copro)}
                          className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded"
                          title="Modifier"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {/* Session 75 : Corbeille visible uniquement pour Secrétaire/Gestionnaire/Admin SaaS */}
                        {canDeleteCoproprietaire && (
                          <button
                            onClick={() => openDeleteConfirm(copro)}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modales */}
      {renderFormModal()}
      {renderImportModal()}
      {renderDetailModal()}
      {renderCreateAccountModal()}
      {renderDeleteConfirmModal()}
      {renderDeleteAllConfirmModal()}
    </div>
  );
};

export default CoproprietairesPage;
